import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, LogOut, ArrowRight, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { relatedJournalsService, authService } from '../services/api';

function AdminRelatedJournals() {
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '', field: '', description: '' });

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await relatedJournalsService.getAll();
        if (response.data) {
          setJournals(response.data);
        }
      } catch (err) {
        setError('خطا در دریافت نشریات مرتبط');
      } finally {
        setLoading(false);
      }
    };
    fetchJournals();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let updated;
      if (editingId) {
        const response = await relatedJournalsService.update(editingId, formData);
        updated = journals.map(j => j.id === editingId ? response.data : j);
      } else {
        const response = await relatedJournalsService.create(formData);
        updated = [...journals, response.data];
      }
      setJournals(updated);
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', url: '', field: '', description: '' });
      setError('');
    } catch (err) {
      setError('خطا در ذخیره نشریه');
    }
  };

  const handleEdit = (journal) => {
    setFormData({ name: journal.name, url: journal.url, field: journal.field, description: journal.description });
    setEditingId(journal.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await relatedJournalsService.delete(id);
      setJournals(journals.filter(j => j.id !== id));
    } catch (err) {
      setError('خطا در حذف نشریه');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', url: '', field: '', description: '' });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت نشریات مرتبط</h1>
              <p className="text-xl text-blue-100">ویرایش نشریات علمی مرتبط</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {!showForm && (
          <button onClick={() => setShowForm(true)} className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg">
            <Plus size={20} />
            افزودن نشریه جدید
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingId ? 'ویرایش نشریه' : 'افزودن نشریه جدید'}</h2>
              <button type="button" onClick={handleCancel} className="p-2 hover:bg-gray-200 rounded-lg transition">
                <X size={24} className="text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-900 mb-2">نام نشریه</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">حوزه</label>
                <input type="text" value={formData.field} onChange={e => setFormData({ ...formData, field: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">آدرس سایت</label>
                <input type="text" value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیحات</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button type="submit" className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
              <button type="button" onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {journals.length ? journals.map(journal => (
            <div key={journal.id} className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{journal.name}</h3>
                  <p className="text-sm text-blue-700">{journal.field}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">{journal.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <a href={journal.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:text-blue-900 font-semibold">{journal.url}</a>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(journal)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                  <button onClick={() => handleDelete(journal.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          )) : (
            <div className="col-span-full bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
              <p>هیچ نشریه‌ای برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminRelatedJournals;
